import type { ElationReportRead } from "@ext/elation/elationReportsRead";
import type { WriteBackRequest, WriteBackResult } from "@ext/elation/elationWriteBack";

/** Message envelope from extension shell → iframe */
export interface ExtensionMessage<T = unknown> {
  source: "astrana-extension";
  type: ExtensionMessageType;
  payload: T;
}

/** Message envelope from iframe → extension shell */
export interface IframeMessage<T = unknown> {
  source: "astrana-standalone";
  type: IframeMessageType;
  payload: T;
}

export type ExtensionMessageType =
  | "AUTH_TOKEN"
  | "AUTH_CLEARED"
  // Shell → iframe hold signal: a session restore (silent SSO / token refresh)
  // is in flight on reopen. Tells the bridge to keep the spinner up and NOT give
  // up to the login screen while it waits — the gap that flashed login on reopen
  // when a silent restore ran longer than the fixed fallback. Always followed by
  // AUTH_TOKEN (restored) or AUTH_CLEARED (genuinely signed out).
  | "AUTH_RESTORING"
  | "ELATION_DATA"
  | "ELATION_WRITE_RESULT"
  | "EXTENSION_CONFIG";

export type IframeMessageType =
  | "IFRAME_READY"
  | "REQUEST_TOKEN_REFRESH"
  | "REQUEST_ELATION_REFRESH"
  | "WRITE_ELATION"
  | "REQUEST_LOGIN"
  | "REQUEST_LOGOUT"
  | "ELATION_SCRAPE_RECIPE"
  | "REQUEST_APP_RELOAD"
  | "REQUEST_PRINT_DOCUMENT";

/**
 * Ask the extension shell to open a self-contained HTML document in a real
 * browser tab for printing / Save-as-PDF. Needed because `window.print()` does
 * not work from inside the Chrome side-panel iframe (the print dialog is bound
 * to a top-level tab, not the panel). Carries PHI (rendered member/claim data),
 * so it MUST be posted to the captured extension origin, never "*".
 */
export interface PrintDocumentPayload {
  /** Full, self-contained HTML document (inline styles, no external refs). */
  html: string;
  /** Optional tab/window title hint. */
  title?: string;
}

export interface AuthTokenPayload {
  accessToken: string;
  expiresAt: number;
  account: { name: string; username: string } | null;
}

/**
 * One ACTUAL section of the currently-open note, read live from the page
 * (`window.el8`). Powers the per-section write-back UI (ACT-1242) — each write
 * targets `key` exactly, so there is no `mapReferenceFn` guessing.
 */
export interface EhrNoteSection {
  /** Exact `_inputSections` key (lowercase, e.g. "assessplan"). */
  key: string;
  /** Pretty label from `getName()` (or the key as a fallback). */
  label: string;
  /** The section's current content, shown as muted read-only context. */
  currentText: string;
  /** False for STRUCTURED sections (orders, meds, etc.) — not free-text writable (ACT-1242). */
  writable?: boolean;
}

/**
 * One Elation visit note — OPEN, signed, or an unsigned draft.
 *
 * Was `ElationSOAPNote`, which was a misnomer: nothing here is SOAP-specific.
 * The same shape carries Simple Note, SOAP and every other template, and the
 * name made each use site read as if it were scoped to one of them.
 */
export interface ElationNote {
  status: "open" | "signed" | "draft";
  /** Numeric Elation note id, when known (open note only, for write-back). */
  noteId?: string;
  visitDate: string;
  visitType: string;
  provider: string;
  examReason: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  ros: string;
  pe: string;
  vitals: Record<string, string>;
  problemList: string[];
  followUp: string;
  /**
   * Per-section write-back (ACT-1242). The note's real sections, read live from
   * the page MAIN world, and which note engine is open. Present on the OPEN note
   * only. `engine !== "legacy"` (or absent sections) means the per-section boxes
   * are not shown (VN2 / nonvisit → "coming soon" state).
   */
  sections?: EhrNoteSection[];
  engine?: "legacy" | "vn2" | "nonvisit" | "none";
  /**
   * Finer status for a LISTED chart note, from `el8.ptdata.visitNotes` (see
   * `extension/elation/elationChartNotes.ts`). `status` deliberately keeps its existing
   * three-value union — it is put on the wire by `@astrana/ui`'s note snapshot —
   * so "on-hold" travels here instead of widening it. Absent on DOM-scraped feed
   * notes and on the open note.
   */
  noteStatus?: "signed" | "draft" | "on-hold";
  /**
   * The note's Elation template ("Simple Note", "SOAP Note", …). Listed notes
   * get it from the model's `getTemplate()`; the OPEN note from
   * `getCurrFormTemplate().displayName`. Nothing else on the page tells the
   * panel which note type is open.
   */
  templateName?: string;
}

export interface ElationClinical {
  openNote: ElationNote | null;
  /**
   * SIGNED notes as scraped from the rendered chart feed. Unchanged meaning and
   * unchanged source — it also feeds `priorNotes` to the backend via
   * `toEhrEncounterSnapshot`, so drafts must NOT start arriving here silently.
   */
  signedNotes: ElationNote[];
  /**
   * Every visit note the Notes tab should LIST — signed notes plus the unsigned
   * drafts / on-hold notes the chart feed can never show (`shouldListChartNote`).
   * Read from Elation's client-side model; falls back to exactly `signedNotes`
   * when that model read is unavailable, so consumers can render it directly.
   */
  chartNotes?: ElationNote[];
  /**
   * Whether `chartNotes` (or its `signedNotes` fallback) is a FACT about the
   * chart, or merely what the reads managed to produce.
   *
   *  - `"loaded"` — the patient-guarded model read answered. An empty list means
   *    THIS CHART HAS NO NOTES, and this is the ONLY value licensed to say so.
   *  - `"unverified"` — the model read did not answer, but the DOM feed produced
   *    at least one note. The rows are real; their patient attribution is not
   *    established (the feed scrape carries none). Render them, but never treat
   *    the list as a verified fact about the chart.
   *  - `"unread"` — neither source could tell us. An empty list means NOTHING.
   *    The Notes tab must say it is still reading, never "no visit notes".
   *  - ABSENT — an extension older than this field, which cannot distinguish "no
   *    notes" from "no read". Consumers FAIL CLOSED: never claim the chart is
   *    empty off it (that claim is what put the chart-switch vanish bug in front
   *    of QA), and settle on the explicit could-not-confirm state instead — see
   *    `ChartNotesPanel`, which owns that rendering and its escalation timer.
   *  - Any UNRECOGNISED value (a newer extension than this app) is treated
   *    exactly like `"unread"`/absent: unknown must never widen a claim.
   *
   * Computed on the extension side by `chartNotesListStatus`, which owns the rule
   * and the reasoning behind it.
   */
  chartNotesStatus?: "loaded" | "unverified" | "unread";
  /**
   * The chart's IMAGING reports, already scrubbed and patient-guarded by
   * `buildElationReports`. Feeds the visit-note drafter's imaging picker.
   *
   * The HOST's shape, not `@astrana/ui`'s `ImagingStudy` — deliberately, for the
   * same reason `chartNotes` carries `ElationNote`: this type is the bridge's
   * wire contract and must not depend on the component library. The conversion
   * to the library's shape happens at the UI boundary, in
   * `imagingStudiesFromReports`.
   *
   * Undefined means NO READ HAPPENED (outside the extension, injection blocked,
   * or the model unreachable). It does not mean "this patient has no imaging",
   * and a consumer must not render an empty state off it — the read's own
   * `available` flag carries that distinction and the picker simply offers
   * nothing.
   *
   * Imaging only: labs reach the note through the backend's results picker, and
   * carrying every lab row across the bridge would move a lot of result data for
   * a feature that never reads it.
   */
  imagingReports?: ElationReportRead[];
  problemList: string[];
  allergies: string[];
  medications: string[];
  careGaps?: string[];
  intolerances?: string[];
  immunizations?: string[];
  pastProblems?: string[];
  insurance?: string[];
}

export interface ElationDataPayload {
  data: ElationPatient | null;
  status: "connected" | "connecting" | "disconnected" | "no-tab" | "error" | "permission-needed";
  clinical?: ElationClinical | null;
  /**
   * Whether the scraped Elation tab is the ACTIVE (foreground) tab. The app
   * gates auto-navigation on this so it never jumps to a patient open only in a
   * background tab.
   */
  sourceTabActive?: boolean;
}

/** iframe → shell: a write-back request, correlated by `requestId`. */
export interface ElationWriteRequestPayload {
  requestId: string;
  req: WriteBackRequest;
}

/** shell → iframe: the write-back result for the matching `requestId`. */
export interface ElationWriteResultPayload {
  requestId: string;
  result: WriteBackResult;
}

export interface ElationPatient {
  patientName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dob?: string | null;
  age?: string | null;
  sex?: string | null;
  primaryPhysician?: string | null;
  phone?: string | null;
  riskScore?: string | null;
  /** EHR-side patient id (Elation: `/patient/{id}/`; eCW: patient-hub account no). */
  ehrPatientId?: string | null;
  /**
   * Which EHR this chart was scraped from (adapter id, e.g. "EMR_ELATION" /
   * "EMR_ECW"). Absent from older extensions — consumers default to Elation.
   */
  sourceSystem?: string | null;
}

export interface ExtensionConfigPayload {
  environment: "production" | "staging" | "qa" | "development";
  extensionVersion: string;
}

export function isExtensionMessage(data: unknown): data is ExtensionMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as ExtensionMessage).source === "astrana-extension"
  );
}

export function isEmbeddedInExtension(): boolean {
  try {
    return window !== window.parent;
  } catch {
    return true; // cross-origin parent = embedded
  }
}
